import React, { FC, ReactNode } from 'react';
import styled from 'styled-components';

export interface LegacyVisibaCortexLayoutProp {
	className?: string;
	alignment?: AlignmentStrUnionMatrix | AlignmentStrUnionExtra;
	direction?: DirectionStrUnion;
	height?: string | number | MaxMin;
	width?: string | number | MaxMin;
	grow?: boolean;
	shrink?: boolean;
	background?: string;
	cornerRadius?: number | string;
	padding?: number | string;
	margin?: number | string;
	shadow?: string;
	box?: BoxSizingUnion;
	clip?: ClipStrUnion;
	children?: ReactNode;
}

interface SanitizedStyleProps {
	$styleAlignment: AlignmentStrUnionMatrix | AlignmentStrUnionExtra;
	$styleDirection?: DirectionStrUnion;
	$styleHeight?: string | number | MaxMin;
	$styleWidth?: string | number | MaxMin;
	$styleGrow?: boolean;
	$styleShrink?: boolean;
	$styleBackground?: string;
	$styleCornerRadius?: number | string;
	$stylePadding?: number | string;
	$styleMargin?: number | string;
	$styleShadow?: string;
	$styleBox?: BoxSizingUnion;
	$styleClip?: ClipStrUnion;
}

type StringOrNumberOrNull = string | number | null;
type MaxMin = [StringOrNumberOrNull, number?];
type DirectionStrUnion = 'row' | 'column';
type ClipStrUnion = 'y' | 'x' | 'xy' | 'hide';
type BoxSizingUnion = 'outer' | 'inner';
// prettier-ignore
type AlignmentStrUnionMatrix =
	| 'topLeading'
	| 'top'
	| 'topTrailing'
	| 'leading'
	| 'center'
	| 'trailing'
	| 'bottomLeading'
	| 'bottom'
	| 'bottomTrailing';

type AlignmentStrUnionExtra = 'spaceCenter' | 'spaceStart' | 'spaceEnd' | 'stretch';

function toCssSize(value: string | number): string {
	return typeof value === 'string' ? value : `${value}px`;
}

const elements = {
	body: styled.div<SanitizedStyleProps>`
		display: flex;
		box-sizing: ${({ $styleBox }) => ($styleBox === 'inner' ? 'border-box' : 'content-box')};
		flex: ${({ $styleGrow }) => ($styleGrow ? '1' : null)};
		flex-shrink: ${({ $styleShrink }) => ($styleShrink ? '1' : '0')};
		padding: ${({ $stylePadding }) => ($stylePadding !== undefined ? toCssSize($stylePadding) : '0')};
		margin: ${({ $styleMargin }) => ($styleMargin !== undefined ? toCssSize($styleMargin) : '0')};
		background: ${({ $styleBackground }) => $styleBackground ?? null};
		border-radius: ${({ $styleCornerRadius }) => ($styleCornerRadius ? toCssSize($styleCornerRadius) : null)};
		box-shadow: ${({ $styleShadow }) => $styleShadow ?? null};

		${({ $styleWidth }) => {
			if (typeof $styleWidth === 'number') {
				return `width: ${$styleWidth}px;`;
			}

			if (Array.isArray($styleWidth)) {
				const [max, min] = $styleWidth as MaxMin;
				let builder = 'width: 100%;';

				if (max !== null) {
					builder += `\nmax-width: ${max}px;`;
				} else {
					builder = '';
				}

				if (min !== undefined) {
					builder += `\nmin-width: ${min}px;`;
				}

				return builder;
			}

			if (typeof $styleWidth === 'string') {
				return `width: ${$styleWidth};`;
			}

			return 'width: 100%;';
		}}

		${({ $styleHeight }) => {
			if (typeof $styleHeight === 'number') {
				return `height: ${$styleHeight}px;`;
			}

			if (Array.isArray($styleHeight)) {
				const [max, min] = $styleHeight as MaxMin;
				let builder = 'height: 100%;';

				if (max !== null) {
					builder += `\nmax-height: ${max}px;`;
				} else {
					builder = '';
				}

				if (min !== undefined) {
					builder += `\nmin-height: ${min}px;`;
				}

				return builder;
			}

			if (typeof $styleHeight === 'string') {
				return `height: ${$styleHeight};`;
			}

			return 'height: 100%;';
		}}

		${({ $styleDirection }) => {
			if ($styleDirection !== undefined) {
				return `flex-direction: ${$styleDirection};`;
			}

			return null;
		}}

		${({ $styleClip }) => {
			if ($styleClip === undefined) {
				return null;
			}

			if ($styleClip === 'x') {
				return 'overflow-x: auto;\noverflow-y: hidden;';
			}

			if ($styleClip === 'y') {
				return 'overflow-y: auto;\noverflow-x: hidden;';
			}

			if ($styleClip === 'xy') {
				return 'overflow: auto;';
			}

			return 'overflow: hidden;';
		}}

		${({ $styleAlignment, $styleDirection }) => {
			if ($styleDirection === 'row') {
				switch ($styleAlignment) {
					case 'center':
						return `
							justify-content: center;
							align-items: center;
						`;
					case 'leading':
						return `
							justify-content: flex-start;
							align-items: center;
						`;
					case 'trailing':
						return `
							justify-content: flex-end;
							align-items: center;
						`;
					case 'top':
						return `
							justify-content: center;
							align-items: flex-start;
						`;
					case 'bottom':
						return `
							justify-content: center;
							align-items: flex-end;
						`;
					case 'topLeading':
						return `
							justify-content: flex-start;
							align-items: flex-start;
						`;
					case 'topTrailing':
						return `
							justify-content: flex-end;
							align-items: flex-start;
						`;
					case 'bottomLeading':
						return `
							justify-content: flex-start;
							align-items: flex-end;
						`;
					case 'bottomTrailing':
						return `
							justify-content: flex-end;
							align-items: flex-end;
						`;
					case 'spaceCenter':
						return `
							justify-content: space-between;
							align-items: center;
						`;
					case 'spaceEnd':
						return `
							justify-content: space-between;
							align-items: flex-end;
						`;
					case 'spaceStart':
						return `
							justify-content: space-between;
							align-items: flex-start;
						`;
					case 'stretch':
						return `
							justify-content: stretch;
							align-items: stretch;
						`;
					default:
						throw new Error('unsupported alignment');
				}
			} else {
				switch ($styleAlignment) {
					case 'center':
						return `
							justify-content: center;
							align-items: center;
						`;
					case 'leading':
						return `
							justify-content: center;
							align-items: flex-start;
						`;
					case 'trailing':
						return `
							justify-content: center;
							align-items: flex-end;
						`;
					case 'top':
						return `
							justify-content: flex-start;
							align-items: center;
						`;
					case 'bottom':
						return `
							justify-content: flex-end;
							align-items: center;
						`;
					case 'topLeading':
						return `
							justify-content: flex-start;
							align-items: flex-start;
						`;
					case 'topTrailing':
						return `
							justify-content: flex-start;
							align-items: flex-end;
						`;
					case 'bottomLeading':
						return `
							justify-content: flex-end;
							align-items: flex-start;
						`;
					case 'bottomTrailing':
						return `
							justify-content: flex-end;
							align-items: flex-end;
						`;
					case 'spaceCenter':
						return `
							justify-content: space-between;
							align-items: center;
						`;
					case 'spaceEnd':
						return `
							justify-content: space-between;
							align-items: flex-end;
						`;
					case 'spaceStart':
						return `
							justify-content: space-between;
							align-items: flex-start;
						`;
					case 'stretch':
						return `
							justify-content: stretch;
							align-items: stretch;
						`;
					default:
						throw new Error('unsupported alignment');
				}
			}
		}}
	`,
};

/**
 * # cortex:react-ui:Layout
 * A layout component that's used to create a basic container with common
 * occurring constraints.
 */
export const LegacyLayout: FC<LegacyVisibaCortexLayoutProp> = ({
	alignment = 'topLeading',
	direction = 'column',
	shrink = true,
	box = 'inner',
	background,
	cornerRadius,
	grow,
	height,
	padding,
	shadow,
	width,
	margin,
	clip,
	className,
	children,
	...rest
}) => {
	return (
		<elements.body
			$styleAlignment={alignment}
			$styleDirection={direction}
			$styleShrink={shrink}
			$styleBox={box}
			$styleBackground={background}
			$styleCornerRadius={cornerRadius}
			$styleGrow={grow}
			$styleHeight={height}
			$stylePadding={padding}
			$styleShadow={shadow}
			$styleWidth={width}
			$styleMargin={margin}
			$styleClip={clip}
			className={className}
			{...rest}
		>
			{children}
		</elements.body>
	);
};
